import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { formatDate } from '../utils/date';
import {
  getSchedule, getUsers, createShift, updateShift, deleteShift,
  getCalendarStatus, startCalendarOAuth, disconnectCalendar, getCalendarEvents,
} from '../services/api';
import { Button } from './ui/button';
import { Input } from './ui/input';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from './ui/select';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from './ui/dialog';
import {
  ChevronLeft, ChevronRight, Plus, MapPin, Trash2, Wrench, AlertTriangle,
  Calendar as CalendarLucide, Link2Off,
} from 'lucide-react';
import { toast } from 'sonner';
import { useNavigate, useSearchParams } from 'react-router-dom';
import {
  rangeForView, weekDays, monthGrid, addDays, addMonths,
  startOfWeek, startOfMonth, sameDay, isoOf, cmpTime, hhmmToHours,
} from '../lib/calendarUtils';
import { colorForCustomer, initialsFor } from '../lib/scheduleColors';

// "any customer / any engineer" sentinel — Shadcn Select rejects empty strings.
const ANY = '__any__';

// Hours visible on the day/week timeline. 06:00 → 22:00 covers 99% of
// the engineer working day; outside-hours shifts still render but get
// clipped (rare and intentional — exposes data quality issues fast).
const TIMELINE_START = 6;
const TIMELINE_END = 22;
const HOUR_HEIGHT = 48; // px per hour

const VIEWS = [
  { value: 'day',   label: 'Day' },
  { value: 'month', label: 'Month' },
];

/**
 * Shared schedule calendar. Used by both the admin (full filters, edit
 * dialog) and employee (own shifts only, read-only) views.
 *
 * Props:
 *   role           — 'admin' | 'employee'
 *   currentUserId  — used by the employee view to scope filters to self
 */
const ScheduleCalendar = ({ role = 'admin', currentUserId = '' }) => {
  const isAdmin = role === 'admin';
  const navigate = useNavigate();

  const [view, setView] = useState('month');
  const [anchor, setAnchor] = useState(() => new Date());
  const [customerId, setCustomerId] = useState(ANY);
  const [engineerId, setEngineerId] = useState(isAdmin ? ANY : currentUserId);
  const [allUsers, setAllUsers] = useState([]);
  const [shifts, setShifts] = useState([]);
  const [loading, setLoading] = useState(false);
  const [editTarget, setEditTarget] = useState(null); // { shift } | { newDate, newUserId }
  const [viewTarget, setViewTarget] = useState(null); // shift object for the details popover
  const [dayListTarget, setDayListTarget] = useState(null); // ISO date string for the "all-shifts-on-this-day" dialog

  // Google Calendar pairing — per-user. `gcalEvents` is the read-only
  // overlay of the logged-in user's own Google events for the visible
  // range. We re-fetch when the range changes.
  const [gcalStatus, setGcalStatus] = useState(null);
  const [gcalEvents, setGcalEvents] = useState([]);
  const [searchParams, setSearchParams] = useSearchParams();

  const range = useMemo(() => rangeForView(view, anchor), [view, anchor]);

  const customers = useMemo(
    () => allUsers
      .filter(u => u.role === 'customer' && !u.parentCustomerId)
      .sort((a, b) => (a.company || a.name || '').localeCompare(b.company || b.name || '')),
    [allUsers],
  );
  const engineers = useMemo(
    () => allUsers
      .filter(u => u.role === 'employee' || u.role === 'admin')
      .sort((a, b) => (a.name || '').localeCompare(b.name || '')),
    [allUsers],
  );

  useEffect(() => {
    if (isAdmin) getUsers().then(setAllUsers).catch(() => {});
  }, [isAdmin]);

  // Surface OAuth callback flags as a one-shot toast and clear them from the URL.
  useEffect(() => {
    if (searchParams.get('calendar_connected') === '1') {
      toast.success('Google Calendar paired');
      const next = new URLSearchParams(searchParams);
      next.delete('calendar_connected');
      setSearchParams(next, { replace: true });
    }
    const err = searchParams.get('calendar_error');
    if (err) {
      toast.error(`Google Calendar pairing failed: ${err}`);
      const next = new URLSearchParams(searchParams);
      next.delete('calendar_error');
      setSearchParams(next, { replace: true });
    }
  }, [searchParams, setSearchParams]);

  const refreshGcalStatus = useCallback(async () => {
    try {
      const s = await getCalendarStatus();
      setGcalStatus(s);
    } catch {
      setGcalStatus({ connected: false, configured: false });
    }
  }, []);

  useEffect(() => { refreshGcalStatus(); }, [refreshGcalStatus]);

  // Pull Google events for the active range whenever the user is paired
  // and the range changes. Quietly resets when not paired.
  useEffect(() => {
    let cancelled = false;
    if (!gcalStatus?.connected) {
      setGcalEvents([]);
      return undefined;
    }
    (async () => {
      try {
        const r = await getCalendarEvents(isoOf(range.from), isoOf(range.to));
        if (!cancelled) setGcalEvents(r?.events || []);
      } catch {
        if (!cancelled) setGcalEvents([]);
      }
    })();
    return () => { cancelled = true; };
  }, [gcalStatus?.connected, range.from, range.to]);

  const handlePairGoogle = async () => {
    try {
      const res = await startCalendarOAuth();
      if (res?.authUrl) {
        window.location.href = res.authUrl;
      } else {
        toast.error('Failed to start Google pairing');
      }
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Failed to start Google pairing');
    }
  };

  const handleUnpairGoogle = async () => {
    if (!window.confirm('Disconnect Google Calendar? Your future shifts will stop syncing automatically.')) return;
    try {
      await disconnectCalendar();
      toast.success('Google Calendar disconnected');
      refreshGcalStatus();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Disconnect failed');
    }
  };

  const refresh = useCallback(async () => {
    setLoading(true);
    try {
      const params = { from: isoOf(range.from), to: isoOf(range.to) };
      if (isAdmin) {
        if (customerId !== ANY) params.customerId = customerId;
        if (engineerId !== ANY) params.userIds = engineerId;
      }
      const res = await getSchedule(params);
      setShifts(res || []);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Failed to load schedule');
    } finally {
      setLoading(false);
    }
  }, [range.from, range.to, customerId, engineerId, isAdmin]);

  useEffect(() => { refresh(); }, [refresh]);

  // Drag-and-drop rescheduling. `newStartTime` is null for month-view
  // drops (where we only move the date and keep the existing time).
  // Optimistic update + revert on error so the calendar feels live.
  const handleShiftMove = useCallback(async (shiftId, newDate, newStartTime) => {
    const orig = shifts.find(s => s.id === shiftId);
    if (!orig || orig.source === 'work_order') return;
    let nextStart = newStartTime || orig.shiftStart;
    let nextEnd;
    const startH = hhmmToHours(orig.shiftStart);
    const endH = hhmmToHours(orig.shiftEnd);
    const duration = Math.max(0.25, endH - startH);
    if (newStartTime) {
      const [nh, nm] = newStartTime.split(':').map(Number);
      const total = nh + (nm / 60) + duration;
      const eh = Math.floor(total);
      const em = Math.round((total - eh) * 60);
      nextEnd = `${String(Math.min(23, eh)).padStart(2, '0')}:${String(em).padStart(2, '0')}`;
    } else {
      nextEnd = orig.shiftEnd;
    }
    if (orig.date === newDate && orig.shiftStart === nextStart && orig.shiftEnd === nextEnd) return;
    // Optimistic
    setShifts(prev => prev.map(s => s.id === shiftId ? { ...s, date: newDate, shiftStart: nextStart, shiftEnd: nextEnd } : s));
    try {
      await updateShift(shiftId, { date: newDate, shiftStart: nextStart, shiftEnd: nextEnd });
      toast.success('Shift moved');
    } catch (e) {
      setShifts(prev => prev.map(s => s.id === shiftId ? orig : s));
      toast.error(e?.response?.data?.detail || 'Move failed');
    }
  }, [shifts]);

  // Group shifts by ISO date so each day renders cleanly.
  const shiftsByDate = useMemo(() => {
    const m = new Map();
    shifts.forEach(s => {
      const key = s.date;
      if (!m.has(key)) m.set(key, []);
      m.get(key).push(s);
    });
    // Sort each day by start time
    m.forEach(arr => arr.sort((a, b) => cmpTime(a.shiftStart, b.shiftStart)));
    return m;
  }, [shifts]);

  // For month view we also surface multi-day WO spans on every cell they cover
  const woSpansByDate = useMemo(() => {
    const m = new Map();
    shifts.filter(s => s.source === 'work_order').forEach(s => {
      const start = new Date(s.startByDate + 'T00:00:00');
      const end = new Date(s.finishByDate + 'T00:00:00');
      let cur = new Date(start);
      while (cur <= end) {
        const key = isoOf(cur);
        if (!m.has(key)) m.set(key, []);
        m.get(key).push(s);
        cur = addDays(cur, 1);
      }
    });
    return m;
  }, [shifts]);

  const step = (dir) => {
    if (view === 'day') setAnchor(addDays(anchor, dir));
    else setAnchor(addMonths(anchor, dir));
  };

  const onCellClick = (date) => {
    if (!isAdmin) return;
    setEditTarget({ newDate: isoOf(date), newUserId: engineerId !== ANY ? engineerId : '' });
  };

  const onShiftClick = (shift) => {
    if (shift.source === 'work_order') {
      navigate(isAdmin ? `/admin/work-orders` : `/employee/work-orders`);
      return;
    }
    // Both admin + employee land in the details popover first; admin
    // gets Edit / Delete buttons inside it. Lets you read all the
    // shift info clearly on a phone before deciding to mutate.
    setViewTarget(shift);
  };

  return (
    <div className="space-y-4">
      {/* Toolbar */}
      <div className="bg-white border border-slate-200 rounded-md p-4 flex flex-wrap items-end gap-3" data-testid="schedule-toolbar">
        <div className="min-w-[140px]">
          <label className="text-[11px] uppercase tracking-wider text-slate-500 font-semibold mb-1 block">View</label>
          <Select value={view} onValueChange={setView}>
            <SelectTrigger className="h-9 w-[120px]" data-testid="schedule-view-select">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {VIEWS.map(v => (
                <SelectItem key={v.value} value={v.value} data-testid={`schedule-view-${v.value}`}>{v.label}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>

        {isAdmin && (
          <>
            <div className="flex-1 min-w-[180px]">
              <label className="text-[11px] uppercase tracking-wider text-slate-500 font-semibold mb-1 block">Customer</label>
              <Select value={customerId} onValueChange={setCustomerId}>
                <SelectTrigger className="h-9" data-testid="schedule-customer-select">
                  <SelectValue placeholder="Any customer" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value={ANY}>Any customer</SelectItem>
                  {customers.map(c => (
                    <SelectItem key={c.id} value={c.id}>{c.company || c.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="flex-1 min-w-[180px]">
              <label className="text-[11px] uppercase tracking-wider text-slate-500 font-semibold mb-1 block">Engineer</label>
              <Select value={engineerId} onValueChange={setEngineerId}>
                <SelectTrigger className="h-9" data-testid="schedule-engineer-select">
                  <SelectValue placeholder="Any engineer" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value={ANY}>Any engineer</SelectItem>
                  {engineers.map(e => (
                    <SelectItem key={e.id} value={e.id}>{e.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </>
        )}

        <div className="flex gap-1 ml-auto items-center">
          <Button size="sm" variant="outline" onClick={() => step(-1)} aria-label="Previous" data-testid="schedule-prev">
            <ChevronLeft className="h-4 w-4" />
          </Button>
          <Button size="sm" variant="outline" onClick={() => step(1)} aria-label="Next" data-testid="schedule-next">
            <ChevronRight className="h-4 w-4" />
          </Button>
          {isAdmin && (
            <Button
              size="sm"
              onClick={() => setEditTarget({ newDate: isoOf(anchor), newUserId: engineerId !== ANY ? engineerId : '' })}
              className="bg-slate-900 hover:bg-slate-800 text-white ml-1"
              data-testid="schedule-add-shift"
            >
              <Plus className="h-4 w-4 sm:mr-1.5" /> <span className="hidden sm:inline">Add shift</span>
            </Button>
          )}
          {gcalStatus?.configured && (
            gcalStatus.connected ? (
              <Button
                size="sm"
                variant="outline"
                onClick={handleUnpairGoogle}
                className="text-slate-600 ml-1"
                data-testid="schedule-gcal-unpair"
                title={`Paired with ${gcalStatus.email || 'Google'}`}
              >
                <Link2Off className="h-4 w-4 sm:mr-1.5" />
                <span className="hidden sm:inline">Unpair Google</span>
              </Button>
            ) : (
              <Button
                size="sm"
                variant="outline"
                onClick={handlePairGoogle}
                className="text-blue-700 border-blue-300 hover:bg-blue-50 ml-1"
                data-testid="schedule-gcal-pair"
              >
                <CalendarLucide className="h-4 w-4 sm:mr-1.5" />
                <span className="hidden sm:inline">Pair Google Calendar</span>
              </Button>
            )
          )}
        </div>
      </div>

      <div className="text-sm font-semibold text-slate-700" data-testid="schedule-range-label">
        {range.label}
        {loading && <span className="ml-2 text-xs text-slate-400">Loading…</span>}
      </div>

      {/* Calendar surface */}
      {view === 'day' && (
        <DayView
          date={anchor}
          shifts={shiftsByDate.get(isoOf(anchor)) || []}
          onCellClick={onCellClick}
          onShiftClick={onShiftClick}
          onShiftMove={isAdmin ? handleShiftMove : null}
        />
      )}
      {view === 'month' && (
        <MonthView
          anchor={anchor}
          shiftsByDate={shiftsByDate}
          woSpansByDate={woSpansByDate}
          onCellClick={onCellClick}
          onShiftClick={onShiftClick}
          onShiftMove={isAdmin ? handleShiftMove : null}
          onShowDayList={(iso) => setDayListTarget(iso)}
        />
      )}

      {/* Customer colour legend — only useful when shifts span multiple
          customers, so we hide it when there's just one. */}
      {isAdmin && shifts.length > 0 && <CustomerLegend shifts={shifts} />}

      {/* Google Calendar overlay — read-only list of the logged-in user's
          own Google events for the visible range. Helps the engineer see
          their personal commitments alongside their shifts without ever
          mutating Google. */}
      {gcalStatus?.connected && gcalEvents.length > 0 && (
        <GoogleEventsOverlay events={gcalEvents} email={gcalStatus.email} />
      )}

      {/* Day list dialog — opened by tapping "+N more" on the Month
          view so a busy day's full shift list is one tap away. */}
      {dayListTarget && (
        <DayShiftsListDialog
          dateIso={dayListTarget}
          shifts={shiftsByDate.get(dayListTarget) || []}
          woSpans={woSpansByDate.get(dayListTarget) || []}
          onClose={() => setDayListTarget(null)}
          onPick={(shift) => {
            setDayListTarget(null);
            onShiftClick(shift);
          }}
        />
      )}

      {/* Details dialog (read-first) */}
      {viewTarget && (
        <ShiftDetailsDialog
          shift={viewTarget}
          isAdmin={isAdmin}
          onClose={() => setViewTarget(null)}
          onEdit={() => {
            const shift = viewTarget;
            setViewTarget(null);
            setEditTarget({ shift });
          }}
          onDeleted={() => { setViewTarget(null); refresh(); }}
        />
      )}

      {/* Edit dialog */}
      {editTarget && (
        <ShiftEditDialog
          target={editTarget}
          engineers={engineers}
          customers={customers}
          onClose={(refreshNeeded) => {
            setEditTarget(null);
            if (refreshNeeded) refresh();
          }}
        />
      )}
    </div>
  );
};

// ────────────────────── Day view ──────────────────────
const DayView = ({ date, shifts, onCellClick, onShiftClick, onShiftMove }) => {
  const hours = useMemo(
    () => Array.from({ length: TIMELINE_END - TIMELINE_START + 1 }, (_, i) => TIMELINE_START + i),
    [],
  );
  const planned = shifts.filter(s => s.source !== 'work_order');
  const woShifts = shifts.filter(s => s.source === 'work_order');
  const onDragOver = (e) => { if (onShiftMove) { e.preventDefault(); e.dataTransfer.dropEffect = 'move'; } };
  const onDrop = (hour) => (e) => {
    if (!onShiftMove) return;
    e.preventDefault();
    const shiftId = e.dataTransfer.getData('text/shiftId');
    if (!shiftId) return;
    onShiftMove(shiftId, isoOf(date), `${String(hour).padStart(2, '0')}:00`);
  };
  return (
    <div className="bg-white border border-slate-200 rounded-md overflow-hidden">
      <div className="px-4 py-2 border-b border-slate-200 bg-slate-50 flex items-center justify-between">
        <div className="font-semibold text-slate-900" data-testid="day-view-header">
          {formatDate(date)}
        </div>
        <div className="text-xs text-slate-500">{shifts.length} item{shifts.length === 1 ? '' : 's'}</div>
      </div>
      {woShifts.length > 0 && (
        <div className="px-4 py-2 border-b border-slate-100 space-y-1">
          {woShifts.map(s => <WorkOrderPill key={s.id} shift={s} onClick={() => onShiftClick(s)} />)}
        </div>
      )}
      <div className="relative" style={{ height: hours.length * HOUR_HEIGHT }}>
        {hours.map((h, i) => (
          <div
            key={h}
            className="absolute left-0 right-0 border-t border-slate-100 flex"
            style={{ top: i * HOUR_HEIGHT, height: HOUR_HEIGHT }}
          >
            <div className="w-14 shrink-0 text-[11px] text-slate-400 pr-2 pt-1 text-right tabular-nums">
              {String(h).padStart(2, '0')}:00
            </div>
            <button
              type="button"
              onClick={() => onCellClick(date)}
              onDragOver={onDragOver}
              onDrop={onDrop(h)}
              className="flex-1 hover:bg-blue-50/40 transition-colors text-left"
              aria-label={`Add shift at ${h}:00`}
              data-testid={`day-cell-${h}`}
            />
          </div>
        ))}
        <div className="absolute inset-0 left-14 pointer-events-none">
          {planned.map(s => <ShiftBlock key={s.id} shift={s} onClick={() => onShiftClick(s)} draggable={!!onShiftMove} />)}
        </div>
      </div>
    </div>
  );
};

// ────────────────────── Month view ──────────────────────
const MonthView = ({
  anchor, shiftsByDate, woSpansByDate,
  onCellClick, onShiftClick, onShiftMove, onShowDayList,
}) => {
  const days = useMemo(() => monthGrid(anchor), [anchor]);
  const today = new Date();
  const weekdayHead = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'];
  const onDragOver = (e) => { if (onShiftMove) { e.preventDefault(); e.dataTransfer.dropEffect = 'move'; } };
  const onDrop = (date) => (e) => {
    if (!onShiftMove) return;
    e.preventDefault();
    const shiftId = e.dataTransfer.getData('text/shiftId');
    if (!shiftId) return;
    // Month view keeps the existing time and only moves the date.
    onShiftMove(shiftId, isoOf(date), null);
  };
  return (
    <div className="bg-white border border-slate-200 rounded-md overflow-hidden">
      <div className="grid grid-cols-7 bg-slate-50 border-b border-slate-200">
        {weekdayHead.map(w => (
          <div key={w} className="px-2 py-2 text-[10px] uppercase tracking-wider text-slate-500 text-center font-semibold">{w}</div>
        ))}
      </div>
      <div className="grid grid-cols-7 grid-rows-6">
        {days.map(d => {
          const dayShifts = (shiftsByDate.get(isoOf(d)) || []).filter(s => s.source !== 'work_order');
          const woSpans = woSpansByDate.get(isoOf(d)) || [];
          const isCurMonth = d.getMonth() === anchor.getMonth();
          const isToday = sameDay(d, today);
          return (
            <div
              key={isoOf(d)}
              onDragOver={onDragOver}
              onDrop={onDrop(d)}
              className={`relative min-h-[110px] border-r border-b border-slate-200 last:border-r-0 ${isCurMonth ? '' : 'bg-slate-50/40'}`}
            >
              <button
                type="button"
                onClick={() => onCellClick(d)}
                className="absolute inset-0 p-1.5 text-left transition-colors hover:bg-blue-50/30 cursor-pointer"
                aria-label={`Day ${isoOf(d)}`}
                data-testid={`month-cell-${isoOf(d)}`}
              />
              <div className="relative pointer-events-none p-1.5">
                <div className="flex items-center justify-between mb-1">
                  <span className={`text-xs font-semibold ${isToday ? 'bg-blue-700 text-white rounded-full w-5 h-5 flex items-center justify-center' : isCurMonth ? 'text-slate-900' : 'text-slate-400'}`}>
                    {d.getDate()}
                  </span>
                  {(dayShifts.length + woSpans.length) > 0 && (
                    <span className="text-[10px] text-slate-400">{dayShifts.length + woSpans.length}</span>
                  )}
                </div>
                <div className="space-y-0.5 pointer-events-auto">
                  {woSpans.slice(0, 1).map(s => (
                    <span
                      key={s.id}
                      role="button"
                      tabIndex={0}
                      onClick={(ev) => { ev.stopPropagation(); onShiftClick(s); }}
                      className="block text-[10px] px-1 py-0.5 rounded bg-amber-100 border border-amber-300 text-amber-900 truncate hover:bg-amber-200 cursor-pointer"
                      title={`${s.workOrderTitle}${s.assetName ? ` · ${s.assetName}` : ''} · ${s.startByDate} → ${s.finishByDate}`}
                    >
                      <Wrench className="inline h-2.5 w-2.5 mr-0.5" />
                      {s.workOrderTitle}{s.assetName ? ` · ${s.assetName}${s.assetNames && s.assetNames.length > 1 ? ` +${s.assetNames.length - 1}` : ''}` : ''}
                    </span>
                  ))}
                  {dayShifts.slice(0, 3).map(s => {
                    const c = colorForCustomer(s.customerId);
                    return (
                      <span
                        key={s.id}
                        role="button"
                        tabIndex={0}
                        draggable={!!onShiftMove}
                        onDragStart={(ev) => {
                          if (!onShiftMove) return;
                          ev.dataTransfer.effectAllowed = 'move';
                          ev.dataTransfer.setData('text/shiftId', s.id);
                        }}
                        onClick={(ev) => { ev.stopPropagation(); onShiftClick(s); }}
                        className={`block text-[10px] text-white px-1 py-0.5 rounded ${c.bg} truncate cursor-pointer hover:opacity-90 ${onShiftMove ? 'cursor-grab active:cursor-grabbing' : ''}`}
                        title={`${s.userName} · ${s.shiftStart}-${s.shiftEnd} · ${s.customerName || 'Lutrom HQ'}`}
                        data-testid={`month-shift-${s.id}`}
                      >
                        <span className="font-semibold mr-1">{initialsFor(s.userName)}</span>
                        <span className="opacity-90">{s.shiftStart}</span>
                      </span>
                    );
                  })}
                  {(dayShifts.length + woSpans.length) > 4 && (
                    <button
                      type="button"
                      onClick={(ev) => { ev.stopPropagation(); onShowDayList?.(isoOf(d)); }}
                      className="block text-[10px] text-blue-700 font-medium hover:underline"
                      data-testid={`month-more-${isoOf(d)}`}
                    >
                      +{(dayShifts.length + woSpans.length) - 4} more
                    </button>
                  )}
                </div>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
};

// ────────────────────── Shift block (timeline) ──────────────────────
const ShiftBlock = ({ shift, onClick, compact = false, draggable = false }) => {
  const startH = hhmmToHours(shift.shiftStart);
  const endH = hhmmToHours(shift.shiftEnd);
  // Clip to visible window so an out-of-range shift still nudges into view.
  const top = Math.max(0, (startH - TIMELINE_START)) * HOUR_HEIGHT;
  const heightHours = Math.max(0.5, (endH || (startH + 1)) - startH);
  const height = heightHours * HOUR_HEIGHT - 2;
  const c = colorForCustomer(shift.customerId);
  return (
    <button
      type="button"
      onClick={onClick}
      draggable={draggable}
      onDragStart={(e) => {
        if (!draggable) return;
        e.dataTransfer.effectAllowed = 'move';
        e.dataTransfer.setData('text/shiftId', shift.id);
      }}
      className={`absolute left-1 right-1 rounded text-white text-xs px-2 py-1 ${c.bg} ring-1 ${c.ring} pointer-events-auto hover:brightness-110 active:scale-[.99] transition shadow-sm overflow-hidden text-left ${draggable ? 'cursor-grab active:cursor-grabbing' : ''}`}
      style={{ top, height }}
      data-testid={`shift-block-${shift.id}`}
      title={`${shift.userName} · ${shift.shiftStart}-${shift.shiftEnd} · ${shift.customerName || 'Lutrom HQ'}${draggable ? ' · drag to reschedule' : ''}`}
    >
      <div className="flex items-center gap-1 font-semibold leading-tight">
        <span className="bg-white/20 rounded px-1 text-[10px]">{initialsFor(shift.userName)}</span>
        <span className="truncate">{shift.shiftStart}–{shift.shiftEnd}</span>
      </div>
      {!compact && shift.customerName && (
        <div className="truncate text-[11px] opacity-90 mt-0.5">{shift.customerName}</div>
      )}
      {shift.site && (
        <div className="truncate text-[10px] opacity-80 mt-0.5 flex items-center gap-0.5">
          <MapPin className="h-2.5 w-2.5 shrink-0" /> {shift.site}
        </div>
      )}
    </button>
  );
};

// ────────────────────── WO span pill ──────────────────────
const WorkOrderPill = ({ shift, onClick }) => (
  <button
    type="button"
    onClick={onClick}
    className="w-full text-left text-xs px-2 py-1.5 rounded bg-amber-50 border border-amber-300 text-amber-900 hover:bg-amber-100 flex items-start gap-2"
    data-testid={`wo-pill-${shift.id}`}
  >
    <AlertTriangle className="h-3.5 w-3.5 mt-0.5 shrink-0" />
    <div className="min-w-0 flex-1">
      <div className="font-semibold truncate">{shift.workOrderTitle}</div>
      <div className="text-[11px] opacity-80">
        {shift.startByDate} → {shift.finishByDate}{shift.userName ? ` · ${shift.userName}` : ''}{shift.customerName ? ` · ${shift.customerName}` : ''}{shift.assetName ? ` · ${shift.assetName}${shift.assetNames && shift.assetNames.length > 1 ? ` +${shift.assetNames.length - 1}` : ''}` : ''}
      </div>
    </div>
  </button>
);

// ────────────────────── Customer legend ──────────────────────
const CustomerLegend = ({ shifts }) => {
  const customers = useMemo(() => {
    const map = new Map();
    shifts.filter(s => s.source !== 'work_order' && s.customerId).forEach(s => {
      if (!map.has(s.customerId)) map.set(s.customerId, s.customerName || 'Customer');
    });
    return Array.from(map.entries()).map(([id, name]) => ({ id, name, color: colorForCustomer(id) }));
  }, [shifts]);
  if (customers.length === 0) return null;
  return (
    <div className="bg-white border border-slate-200 rounded-md p-3 flex flex-wrap items-center gap-2 text-xs" data-testid="schedule-legend">
      <span className="text-slate-500 mr-1">Customers:</span>
      {customers.map(c => (
        <span key={c.id} className={`inline-flex items-center gap-1.5 px-2 py-0.5 rounded ${c.color.soft}`}>
          <span className={`h-2 w-2 rounded-full ${c.color.bg}`} />
          {c.name}
        </span>
      ))}
    </div>
  );
};

// ────────────────────── Day list dialog ──────────────────────
// Triggered by tapping "+N more" on a Month-view cell. Surfaces every
// shift + WO span on the day in a scrollable list — each row is itself
// a button that drops into the existing details / edit flow, so it
// works as a fast scanner on busy customer days without forcing the
// user into Day view.
const DayShiftsListDialog = ({ dateIso, shifts, woSpans, onClose, onPick }) => {
  const fmtDate = (() => {
    try {
      return new Date(dateIso + 'T00:00:00').toLocaleDateString('en-GB', {
        weekday: 'long', day: 'numeric', month: 'long', year: 'numeric',
      });
    } catch {
      return dateIso;
    }
  })();
  const planned = shifts
    .filter(s => s.source !== 'work_order')
    .sort((a, b) => cmpTime(a.shiftStart, b.shiftStart));
  const total = planned.length + woSpans.length;
  return (
    <Dialog open onOpenChange={(o) => { if (!o) onClose(); }}>
      <DialogContent className="sm:max-w-md" data-testid="day-list-dialog">
        <DialogHeader>
          <DialogTitle>{fmtDate}</DialogTitle>
          <DialogDescription className="sr-only">
            All shifts and work-order windows on this day.
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-1.5 max-h-[60vh] overflow-y-auto -mx-2 px-2">
          {total === 0 && (
            <div className="text-sm text-slate-500 italic py-3">Nothing scheduled.</div>
          )}
          {woSpans.map(s => (
            <button
              key={s.id}
              type="button"
              onClick={() => onPick(s)}
              className="w-full text-left rounded border border-amber-300 bg-amber-50 hover:bg-amber-100 px-3 py-2 flex items-start gap-2"
              data-testid={`day-list-wo-${s.id}`}
            >
              <Wrench className="h-4 w-4 text-amber-700 mt-0.5 shrink-0" />
              <div className="min-w-0 flex-1">
                <div className="font-semibold text-amber-900 truncate">{s.workOrderTitle}</div>
                <div className="text-xs text-amber-800/80">
                  {s.startByDate} → {s.finishByDate}{s.userName ? ` · ${s.userName}` : ''}{s.customerName ? ` · ${s.customerName}` : ''}{s.assetName ? ` · ${s.assetName}${s.assetNames && s.assetNames.length > 1 ? ` +${s.assetNames.length - 1}` : ''}` : ''}
                </div>
              </div>
            </button>
          ))}
          {planned.map(s => {
            const c = colorForCustomer(s.customerId);
            return (
              <button
                key={s.id}
                type="button"
                onClick={() => onPick(s)}
                className="w-full text-left rounded border border-slate-200 bg-white hover:bg-slate-50 px-3 py-2 flex items-center gap-3"
                data-testid={`day-list-shift-${s.id}`}
              >
                <span className={`shrink-0 rounded ${c.bg} text-white text-xs font-semibold px-2 py-1`}>
                  {initialsFor(s.userName)}
                </span>
                <div className="min-w-0 flex-1">
                  <div className="font-medium text-slate-900 truncate">
                    {s.userName || 'Unassigned'}
                    <span className="ml-2 text-xs text-slate-500 tabular-nums">
                      {s.shiftStart}–{s.shiftEnd}
                    </span>
                  </div>
                  <div className="text-xs text-slate-500 truncate">
                    {s.customerName || 'Lutrom HQ / internal'}
                    {s.site ? ` · ${s.site}` : ''}
                  </div>
                </div>
              </button>
            );
          })}
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Close</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};


// ────────────────────── Details dialog ──────────────────────
// Roomy read-first popover so admins can scan all the shift info on a
// phone before hitting Edit / Delete. Employees see the same info but
// without the action buttons.
const ShiftDetailsDialog = ({ shift, isAdmin, onClose, onEdit, onDeleted }) => {
  const [deleting, setDeleting] = useState(false);
  const c = colorForCustomer(shift.customerId);

  const fmtDate = (() => {
    try {
      return new Date(shift.date + 'T00:00:00').toLocaleDateString('en-GB', {
        weekday: 'long', day: 'numeric', month: 'long', year: 'numeric',
      });
    } catch {
      return shift.date;
    }
  })();

  const handleDelete = async () => {
    if (!window.confirm('Delete this shift? This cannot be undone.')) return;
    setDeleting(true);
    try {
      await deleteShift(shift.id);
      toast.success('Shift deleted');
      onDeleted();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Delete failed');
    } finally {
      setDeleting(false);
    }
  };

  return (
    <Dialog open onOpenChange={(o) => { if (!o) onClose(); }}>
      <DialogContent className="sm:max-w-md" data-testid="shift-details-dialog">
        <DialogHeader>
          {/* Coloured rail mirrors the customer's calendar block colour
              so admins can immediately verify they tapped the right
              shift on a busy day. */}
          <div className={`-mt-2 -mx-6 mb-3 h-1.5 ${c.bg}`} />
          <DialogTitle className="flex items-start gap-3">
            <span className={`shrink-0 rounded ${c.bg} text-white text-xs font-semibold px-2 py-1`}>
              {initialsFor(shift.userName)}
            </span>
            <span className="min-w-0">
              <span className="block text-base">{shift.userName || 'Unassigned'}</span>
              <span className="block text-xs font-normal text-slate-500 mt-0.5">{fmtDate}</span>
            </span>
          </DialogTitle>
          <DialogDescription className="sr-only">Shift details</DialogDescription>
        </DialogHeader>

        <dl className="text-sm space-y-3">
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-2">
            <dt className="text-xs uppercase tracking-wider text-slate-500 font-semibold">Time</dt>
            <dd className="col-span-2 font-medium tabular-nums">{shift.shiftStart} – {shift.shiftEnd}</dd>
          </div>
          {shift.customerName && (
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-2">
              <dt className="text-xs uppercase tracking-wider text-slate-500 font-semibold">Customer</dt>
              <dd className="col-span-2">{shift.customerName}</dd>
            </div>
          )}
          {shift.site && (
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-2">
              <dt className="text-xs uppercase tracking-wider text-slate-500 font-semibold">Location</dt>
              <dd className="col-span-2 flex items-start gap-1">
                <MapPin className="h-3.5 w-3.5 text-slate-400 mt-0.5 shrink-0" />
                <span>{shift.site}</span>
              </dd>
            </div>
          )}
          {shift.notes && (
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-2">
              <dt className="text-xs uppercase tracking-wider text-slate-500 font-semibold">Notes</dt>
              <dd className="col-span-2 whitespace-pre-wrap">{shift.notes}</dd>
            </div>
          )}
          {!shift.customerName && !shift.site && !shift.notes && (
            <div className="text-xs text-slate-400 italic">No additional details.</div>
          )}
        </dl>

        <DialogFooter className="flex-row justify-between sm:justify-between mt-4">
          {isAdmin ? (
            <Button
              variant="outline"
              onClick={handleDelete}
              disabled={deleting}
              className="text-red-600 border-red-300 hover:bg-red-50"
              data-testid="shift-details-delete"
            >
              <Trash2 className="h-4 w-4 mr-1" /> {deleting ? 'Deleting…' : 'Delete'}
            </Button>
          ) : <span />}
          <div className="flex gap-2">
            <Button variant="outline" onClick={onClose} disabled={deleting}>Close</Button>
            {isAdmin && (
              <Button
                onClick={onEdit}
                disabled={deleting}
                className="bg-slate-900 hover:bg-slate-800 text-white"
                data-testid="shift-details-edit"
              >
                Edit
              </Button>
            )}
          </div>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};


// ────────────────────── Edit dialog ──────────────────────
const ShiftEditDialog = ({ target, engineers, customers, onClose }) => {
  const isEdit = !!target.shift;
  const initialShift = target.shift || {
    userId: target.newUserId || '',
    date: target.newDate || isoOf(new Date()),
    shiftStart: '08:00',
    shiftEnd: '17:00',
    customerId: '',
    site: '',
    notes: '',
  };
  const [form, setForm] = useState(initialShift);
  const [saving, setSaving] = useState(false);
  const [deleting, setDeleting] = useState(false);

  const setField = (k, v) => setForm(f => ({ ...f, [k]: v }));

  const save = async () => {
    if (!form.userId) { toast.error('Pick an engineer'); return; }
    if (!form.date) { toast.error('Pick a date'); return; }
    if (!form.shiftStart || !form.shiftEnd) { toast.error('Pick start and end times'); return; }
    if (form.shiftStart >= form.shiftEnd) { toast.error('End time must be after start'); return; }
    setSaving(true);
    try {
      const payload = {
        userId: form.userId,
        date: form.date,
        shiftStart: form.shiftStart,
        shiftEnd: form.shiftEnd,
        site: form.site || '',
        customerId: form.customerId || '',
        notes: form.notes || '',
      };
      if (isEdit) {
        await updateShift(target.shift.id, payload);
        toast.success('Shift updated');
      } else {
        await createShift(payload);
        toast.success('Shift created');
      }
      onClose(true);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Save failed');
    } finally {
      setSaving(false);
    }
  };

  const remove = async () => {
    if (!isEdit) return;
    if (!window.confirm('Delete this shift? This cannot be undone.')) return;
    setDeleting(true);
    try {
      await deleteShift(target.shift.id);
      toast.success('Shift deleted');
      onClose(true);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Delete failed');
    } finally {
      setDeleting(false);
    }
  };

  return (
    <Dialog open onOpenChange={(o) => { if (!o) onClose(false); }}>
      <DialogContent className="sm:max-w-md" data-testid="shift-edit-dialog">
        <DialogHeader>
          <DialogTitle>{isEdit ? 'Edit shift' : 'Add shift'}</DialogTitle>
          <DialogDescription className="sr-only">
            {isEdit ? 'Update the shift details, or delete it.' : 'Schedule a new shift for an engineer with optional customer and location.'}
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-3">
          <div>
            <label className="text-xs uppercase tracking-wider text-slate-500 font-semibold block mb-1">Engineer</label>
            <Select value={form.userId} onValueChange={(v) => setField('userId', v)}>
              <SelectTrigger className="h-10" data-testid="shift-engineer-select">
                <SelectValue placeholder="Pick an engineer…" />
              </SelectTrigger>
              <SelectContent>
                {engineers.map(e => (
                  <SelectItem key={e.id} value={e.id} data-testid={`shift-engineer-${e.id}`}>{e.name}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
            <div>
              <label className="text-xs uppercase tracking-wider text-slate-500 font-semibold block mb-1">Date</label>
              <Input
                type="date"
                value={form.date}
                onChange={(e) => setField('date', e.target.value)}
                className="h-10 w-full"
                data-testid="shift-date"
              />
            </div>
            <div>
              <label className="text-xs uppercase tracking-wider text-slate-500 font-semibold block mb-1">Start</label>
              <Input
                type="time"
                value={form.shiftStart}
                onChange={(e) => setField('shiftStart', e.target.value)}
                className="h-10 w-full"
                data-testid="shift-start"
              />
            </div>
            <div>
              <label className="text-xs uppercase tracking-wider text-slate-500 font-semibold block mb-1">End</label>
              <Input
                type="time"
                value={form.shiftEnd}
                onChange={(e) => setField('shiftEnd', e.target.value)}
                className="h-10 w-full"
                data-testid="shift-end"
              />
            </div>
          </div>
          <div>
            <label className="text-xs uppercase tracking-wider text-slate-500 font-semibold block mb-1">Customer (optional)</label>
            <Select value={form.customerId || '__none__'} onValueChange={(v) => setField('customerId', v === '__none__' ? '' : v)}>
              <SelectTrigger className="h-10" data-testid="shift-customer-select">
                <SelectValue placeholder="Lutrom HQ / internal" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="__none__">Lutrom HQ / internal</SelectItem>
                {customers.map(c => (
                  <SelectItem key={c.id} value={c.id} data-testid={`shift-customer-${c.id}`}>{c.company || c.name}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div>
            <label className="text-xs uppercase tracking-wider text-slate-500 font-semibold block mb-1">Location / site</label>
            <Input
              value={form.site || ''}
              onChange={(e) => setField('site', e.target.value)}
              placeholder="e.g. Site Alpha — Plant 3"
              className="h-10"
              data-testid="shift-site"
            />
          </div>
          <div>
            <label className="text-xs uppercase tracking-wider text-slate-500 font-semibold block mb-1">Notes</label>
            <Input
              value={form.notes || ''}
              onChange={(e) => setField('notes', e.target.value)}
              placeholder="Optional"
              className="h-10"
              data-testid="shift-notes"
            />
          </div>
        </div>
        <DialogFooter className="flex-row justify-between sm:justify-between">
          {isEdit ? (
            <Button
              variant="outline"
              onClick={remove}
              disabled={saving || deleting}
              className="text-red-600 border-red-300 hover:bg-red-50"
              data-testid="shift-delete"
            >
              <Trash2 className="h-4 w-4 mr-1" /> {deleting ? 'Deleting…' : 'Delete'}
            </Button>
          ) : <span />}
          <div className="flex gap-2">
            <Button variant="outline" onClick={() => onClose(false)} disabled={saving || deleting}>Cancel</Button>
            <Button
              onClick={save}
              disabled={saving || deleting}
              className="bg-slate-900 hover:bg-slate-800 text-white"
              data-testid="shift-save"
            >
              {saving ? 'Saving…' : (isEdit ? 'Save' : 'Add shift')}
            </Button>
          </div>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

// ────────────────────── Google Calendar overlay ──────────────────────
// Read-only list of the logged-in user's personal Google events for the
// visible range. Sits under the calendar so shifts stay the focal point
// but the engineer can still see "oh, I have a dentist 2pm Wed" without
// flipping apps. Outbound sync (shift → Google) means our own shifts
// never appear here (filtered out by lutromShiftId metadata server-side).
const GoogleEventsOverlay = ({ events, email }) => {
  const fmt = (iso, allDay) => {
    if (!iso) return '';
    if (allDay) {
      return `${formatDate(iso)} · all day`;
    }
    const d = new Date(iso);
    const hh = String(d.getHours()).padStart(2, '0');
    const mm = String(d.getMinutes()).padStart(2, '0');
    return `${formatDate(d)} · ${hh}:${mm}`;
  };
  return (
    <div className="bg-white border border-slate-200 rounded-md p-4 space-y-2" data-testid="gcal-overlay">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          <CalendarLucide className="h-4 w-4 text-blue-700" />
          <span className="font-semibold text-slate-900">Your Google Calendar</span>
          {email && <span className="text-xs text-slate-500">· {email}</span>}
        </div>
        <span className="text-xs text-slate-400">{events.length} event{events.length === 1 ? '' : 's'}</span>
      </div>
      <ul className="space-y-1.5">
        {events.map(ev => (
          <li key={ev.id} className="flex items-start gap-2 text-sm">
            <span className="h-2 w-2 rounded-full bg-blue-500 mt-1.5 shrink-0" />
            <div className="min-w-0 flex-1">
              <div className="font-medium text-slate-900 truncate">{ev.summary}</div>
              <div className="text-xs text-slate-500">
                {fmt(ev.start, ev.allDay)}{ev.location ? ` · ${ev.location}` : ''}
              </div>
            </div>
            {ev.htmlLink && (
              <a
                href={ev.htmlLink}
                target="_blank"
                rel="noopener noreferrer"
                className="text-[11px] text-blue-700 hover:underline shrink-0"
              >
                Open
              </a>
            )}
          </li>
        ))}
      </ul>
    </div>
  );
};

export default ScheduleCalendar;
